import { Inject, Injectable, OnDestroy } from '@angular/core';
import { combineLatest, EMPTY, merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { DestinationsProvider } from '@prlw/core/destinations/destinations.provider';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import {
  MONITOR_GATEWAY,
  MonitorGateway,
} from '@prlw/core/monitor/monitor.gateway';
import { Modifier } from '@prlw/core/monitor/types/common';
import { ProductGroupsService } from '@prlw/core/products/product-groups.service';
import { ProductsService } from '@prlw/core/products/products.service';
import { ShipmentsState } from '@prlw/core/shipments/shipments.state';
import { MainTableModifiersService } from './main-table-modifiers.service';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';
import { ShipmentsProvider } from '../shipments/shipments.provider';
import { TARIFFS_GATEWAY, TariffsGateway } from '../tariffs/tariffs.gateway';
import { TariffStatus } from './tariff-status.enum';
import {
  IChannelUpdateDeal,
  IChannelUpdateQuote,
  IChannelUpdateTariff,
  SSE_GATEWAY,
  SseGateway,
} from '../sse/sse.gateway';
import { eventGuard } from '@prlw/libs/event-source/event-source.service';
import { SessionAgeStatus } from './session-age-status.enum';
import {
  asOwpSuccess,
  mapResult,
  observableWithPending,
} from '@prlw/infrastructure/observableWithPending-v2';
import { HttpErrorResponse } from '@angular/common/http';
import { MarketTimeController } from '../market-time/market-time.controller';
import { VisibilityChangeService } from '@prlw/libs/visibility-change/visibility-change.service';

interface IInstrument {
  dataType: 'IInstrument';
  items: Instrument[];
}

@Injectable({
  providedIn: 'root',
})
export class MainTableInstrumentsService implements OnDestroy {
  public readonly instruments$: Observable<Instrument[]>;
  public readonly modifier$: Observable<Modifier>;

  private destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  processIChannelUpdateTariff(
    instrument: Instrument,
    items: IChannelUpdateTariff[],
    modifier: Modifier,
    destinationStationId: number | null,
  ) {
    if (!destinationStationId) return instrument;
    if (items.length) {
      const upd = items.find(
        (item) =>
          instrument.instrumentCode === item.instrumentCode &&
          destinationStationId === item.destinationStationId,
      );
      if (upd) {
        return {
          ...instrument,
          tariff: typeof instrument.tariff === 'number' ? upd.price : null,
          deliveryDays: upd.deliveryTime,
          totalPrice:
            typeof instrument.totalPrice === 'number'
              ? this.mainTableModifiersService.getModificationValuesPriceByParams(
                  instrument.quote.lastDealPrice,
                  upd.price,
                  14,
                  modifier,
                )
              : instrument.totalPrice,
          tariffStatus: TariffStatus.Ready,
        };
      }
    }
    return instrument;
  }

  processIChannelUpdateQuote(
    instrument: Instrument,
    items: IChannelUpdateQuote[],
  ) {
    if (items.length) {
      const upd = items.find(
        (item) => instrument.instrumentCode === item.instrumentCode,
      );
      if (upd) {
        const [best] = upd.orderbook;
        if (upd.type === 'bid') {
          if (!best) {
            return {
              ...instrument,
              quote: {
                ...instrument.quote,
                buyPrice: 0,
                buyAmount: 0,
                bid: upd.orderbook,
                timestamp: upd.timestamp,
                quoteStatus: SessionAgeStatus.Actual,
              },
            };
          }
          return {
            ...instrument,
            quote: {
              ...instrument.quote,
              buyPrice: best[0],
              buyAmount: best[1],
              bid: upd.orderbook,
              timestamp: upd.timestamp,
              quoteStatus: SessionAgeStatus.Actual,
            },
          };
        }
        if (upd.type === 'ask') {
          if (!best) {
            return {
              ...instrument,
              quote: {
                ...instrument.quote,
                sellPrice: 0,
                sellAmount: 0,
                ask: upd.orderbook,
                timestamp: upd.timestamp,
                quoteStatus: SessionAgeStatus.Actual,
              },
            };
          }
          return {
            ...instrument,
            quote: {
              ...instrument.quote,
              sellPrice: best[0],
              sellAmount: best[1],
              ask: upd.orderbook,
              timestamp: upd.timestamp,
              quoteStatus: SessionAgeStatus.Actual,
            },
          };
        }
      }
    }
    return instrument;
  }

  processIChannelUpdateDeal(
    instrument: Instrument,
    items: IChannelUpdateDeal[],
    modifier: Modifier,
  ) {
    if (items.length) {
      const [upd] = items;
      if (upd.instrumentCode !== instrument.instrumentCode) return instrument;
      if (new Date(upd.timestamp) <= new Date(instrument.dealTimestamp))
        return instrument;

      const getDynamic = (item: Instrument, lastDealPrice: number) => {
        const {
          quote: { prevDealPrice },
        } = item;
        if (!prevDealPrice) return 0;
        if (!lastDealPrice) return 0;
        return lastDealPrice - prevDealPrice;
      };

      const getPrevDealsAmountDiff = (item: Instrument, amount: number) => {
        if (item.previousSessionAmount)
          return item.prevDealsAmountDiff + amount;
        return 0;
      };

      return {
        ...instrument,
        quote: {
          ...instrument.quote,
          lastDealPrice: upd.price,
          dealStatus: SessionAgeStatus.Actual,
          delta: getDynamic(instrument, upd.price),
        },
        dealTimestamp: upd.timestamp,
        currentAmount: instrument.currentAmount + upd.amount,
        prevDealsAmountDiff: getPrevDealsAmountDiff(instrument, upd.amount),
        totalPrice:
          instrument.tariff && instrument.shipmentDays
            ? this.mainTableModifiersService.getModificationValuesPriceByParams(
                upd.price,
                instrument.tariff,
                instrument.shipmentDays,
                modifier,
              )
            : instrument.totalPrice,
        isDealTotal:
          instrument.tariff && instrument.shipmentDays
            ? true
            : instrument.isDealTotal,
      };
    }
    return instrument;
  }

  constructor(
    @Inject(MONITOR_GATEWAY) private readonly monitorGateway: MonitorGateway,
    @Inject(TARIFFS_GATEWAY) private readonly tariffsGateway: TariffsGateway,
    @Inject(SSE_GATEWAY) private readonly sseGateway: SseGateway,
    private readonly productGroupsService: ProductGroupsService,
    private readonly destinationsProvider: DestinationsProvider,
    private readonly shipmentsState: ShipmentsState,
    private readonly productsService: ProductsService,
    private readonly mainTableModifiersService: MainTableModifiersService,
    private readonly shipmentsProvider: ShipmentsProvider,
    private readonly responsiveService: ResponsiveService,
    private readonly marketTimeController: MarketTimeController,
    private readonly visibilityChangeService: VisibilityChangeService,
  ) {
    this.modifier$ = mainTableModifiersService.modifier$;
    this.instruments$ = merge(
      this.sseGateway.unicastMessage$,
      this.sseGateway.multicastMessage$,
      this.reqInstrumentsWithPending$.pipe(mapResult),
    ).pipe(
      withLatestFrom(
        destinationsProvider.currentDestination$.pipe(
          map((currentDestination) => {
            if (currentDestination) {
              return Number(currentDestination.id);
            }
            return null;
          }),
        ),
        this.modifier$,
      ),
      scan((acc, [updates, destinationStationId, modifier]) => {
        const arrIInstrument = updates.filter(
          eventGuard<IInstrument>('IInstrument'),
        );
        if (arrIInstrument.length) {
          const [{ items }] = arrIInstrument;
          return items;
        }
        return acc.map((instrument) =>
          this.processIChannelUpdateTariff(
            this.processIChannelUpdateQuote(
              this.processIChannelUpdateDeal(
                instrument,
                updates.filter(
                  eventGuard<IChannelUpdateDeal>('IChannelUpdateDeal'),
                ),
                modifier,
              ),
              updates.filter(
                eventGuard<IChannelUpdateQuote>('IChannelUpdateQuote'),
              ),
            ),
            updates.filter(
              eventGuard<IChannelUpdateTariff>('IChannelUpdateTariff'),
            ),
            modifier,
            destinationStationId,
          ),
        );
      }, [] as Instrument[]),
      map((items) => {
        const sortedLastDeal = items
          .slice()
          .filter(
            (item) =>
              item.quote &&
              item.quote.lastDealPrice > 0 &&
              item.quote.dealStatus === SessionAgeStatus.Actual,
          )
          .sort((a, b) => a.quote.lastDealPrice - b.quote.lastDealPrice);
        const sortedBuy = items
          .slice()
          .filter(
            (item) =>
              item.quote &&
              item.quote.buyPrice > 0 &&
              item.quote.dealStatus === SessionAgeStatus.Actual,
          )
          .sort((a, b) => a.quote.buyPrice - b.quote.buyPrice);
        const sortedSell = items
          .slice()
          .filter(
            (item) =>
              item.quote &&
              item.quote.sellPrice > 0 &&
              item.quote.dealStatus === SessionAgeStatus.Actual,
          )
          .sort((a, b) => a.quote.sellPrice - b.quote.sellPrice);
        const sortedTariff = items
          .slice()
          .filter((item) => item.tariff && item.tariff > 0)
          .sort((a, b) => <number>a.tariff - <number>b.tariff);
        const sortedTotalPrice = items
          .slice()
          .filter(
            (item) =>
              typeof item.totalPrice === 'number' &&
              item.totalPrice > 0 &&
              item.quote &&
              item.quote.dealStatus === SessionAgeStatus.Actual,
          )
          .sort((a, b) => <number>a.totalPrice - <number>b.totalPrice);

        return items.map((item) => ({
          ...item,
          bestLastDeal:
            item.instrumentCode === sortedLastDeal[0]?.instrumentCode,
          bestBuy: item.instrumentCode === sortedBuy[0]?.instrumentCode,
          bestSell: item.instrumentCode === sortedSell[0]?.instrumentCode,
          bestTariff: item.instrumentCode === sortedTariff[0]?.instrumentCode,
          bestTotalPrice:
            item.instrumentCode === sortedTotalPrice[0]?.instrumentCode,
        }));
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
  }

  reqInstrumentsWithPending$ = combineLatest([
    this.productGroupsService.currentProductGroup$,
    this.responsiveService.xsmall$,
  ]).pipe(
    switchMap(([product, xsmall]) =>
      combineLatest([
        this.destinationsProvider.currentDestination$,
        xsmall
          ? this.shipmentsProvider.shipmentsByCurrentProductGroup$.pipe(
              map((shipments) => shipments.map((shipment) => shipment.code)),
            )
          : this.shipmentsState.getSelectedShipments(product.code),
        xsmall
          ? this.productsService.allProductsOfCurrentGroup$
          : this.productsService.selectedProductsOfCurrentGroup$,
        of(product.code),
        this.modifier$,
        this.sseGateway.messageIChannelUpdateTraderTariff$.pipe(startWith(1)),
      ]),
    ),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    switchMap(
      ([destination, shipments, activeProducts, productCode, modifier]) => {
        if (!activeProducts.length) {
          return of(asOwpSuccess([]));
        }

        return observableWithPending(
          merge(
            this.marketTimeController.isTradingActive$,
            this.responsiveService.xsmall$.pipe(
              filter((item) => item === true),
              switchMap(() => this.visibilityChangeService.documentIsActive$),
            ),
          ).pipe(
            switchMap(() =>
              this.monitorGateway
                .getInstruments(
                  productCode,
                  destination?.id ?? null,
                  shipments,
                  activeProducts,
                  modifier,
                )
                .pipe(
                  tap((instruments) => {
                    if (destination?.id) {
                      this.tariffsGateway
                        .updateWaitStatusTariff(
                          instruments
                            .filter(
                              (
                                instrument,
                              ): instrument is Instrument & {
                                tariffStatus: TariffStatus.Wait;
                                productId: number;
                                shipmentBasisId: number;
                              } =>
                                instrument.tariffStatus === TariffStatus.Wait &&
                                Boolean(instrument.productId) &&
                                Boolean(instrument.shipmentBasisId),
                            )
                            .map((instrument) => ({
                              instrumentCode: instrument.instrumentCode,
                              productId: instrument.productId,
                              shipmentBasisId: instrument.shipmentBasisId,
                              destinationStationId: Number(destination.id),
                            })),
                        )
                        .pipe(
                          take(1),
                          catchError((error: HttpErrorResponse) => {
                            if (error.status === 403) return EMPTY;
                            throw error;
                          }),
                        )
                        .subscribe();
                    }
                  }),
                  map((items) => [{ dataType: 'IInstrument', items }]),
                ),
            ),
          ),
        );
      },
    ),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );
}
